import type { ReportHistoryEntryType } from '@api';
import { ActionsCell, formatDateTime, RecipientsSummary } from '@common';
import { DataTableColumn } from 'mantine-datatable';
import { useTranslation } from 'react-i18next';
import {
  GeneratedTableCell,
  ReportHistoryStatusBadge,
  ReportHistoryTriggerBadge,
  ReviewedTableCell,
} from './components';

type UseReportHistoryTableColumnsParamsType = {
  canReview: boolean;
  pendingEntryId: string | undefined;
  onMarkAsReviewed: (entryId: string) => void;
};

export type UseReportHistoryTableColumnsReturnType = {
  columns: DataTableColumn<ReportHistoryEntryType>[];
};

export const useReportHistoryTableColumns = ({
  canReview,
  pendingEntryId,
  onMarkAsReviewed,
}: UseReportHistoryTableColumnsParamsType): UseReportHistoryTableColumnsReturnType => {
  const { t } = useTranslation();

  const columns: DataTableColumn<ReportHistoryEntryType>[] = [
    {
      accessor: 'reportConfigurationLabel',
      title: t('REPORTS.HISTORY.TABLE_COLUMNS.MAILING'),
    },
    {
      accessor: 'date',
      title: t('REPORTS.HISTORY.TABLE_COLUMNS.DATE'),
      render: (record) => formatDateTime(record.date),
    },
    {
      accessor: 'trigger',
      title: t('REPORTS.HISTORY.TABLE_COLUMNS.TRIGGER'),
      render: (record) => (
        <ReportHistoryTriggerBadge trigger={record.trigger} />
      ),
    },
    {
      accessor: 'status',
      title: t('REPORTS.HISTORY.TABLE_COLUMNS.STATUS'),
      render: (record) => <ReportHistoryStatusBadge status={record.status} />,
    },
    {
      accessor: 'generated',
      title: t('REPORTS.HISTORY.TABLE_COLUMNS.GENERATED'),
      render: (record) => <GeneratedTableCell generated={record.generated} />,
    },
    {
      accessor: 'recipients',
      title: t('REPORTS.HISTORY.TABLE_COLUMNS.RECIPIENTS'),
      render: (record) => (
        <RecipientsSummary
          recipients={[
            ...record.personRecipients.map(
              ({ id, firstname, lastname, email }) => ({
                id,
                name: `${firstname} ${lastname}`,
                email,
              })
            ),
            ...record.customRecipients,
          ]}
        />
      ),
    },
    {
      accessor: 'reviewed',
      title: t('REPORTS.HISTORY.TABLE_COLUMNS.REVIEWED'),
      render: (record) => (
        <ReviewedTableCell
          entry={record}
          canReview={canReview}
          isPending={pendingEntryId === record.id}
          onMarkAsReviewed={onMarkAsReviewed}
        />
      ),
    },
    {
      accessor: 'reportDownloadUrl',
      title: t('REPORTS.HISTORY.TABLE_COLUMNS.DOWNLOAD'),
      titleStyle: { textAlign: 'center' },
      width: 100,
      render: ({ reportDownloadUrl }) =>
        reportDownloadUrl && (
          <ActionsCell.Root>
            <ActionsCell.PdfDownload url={reportDownloadUrl} />
          </ActionsCell.Root>
        ),
    },
  ];

  return { columns };
};
